import React from 'react';
import { motion } from 'framer-motion';
import { ArrowCounterClockwise, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Logo2 } from './Logo2';
import { ProgressRail } from './ProgressRail';
import type { Tone } from '../types/snak';
interface SnakShellProps {
  tone: Tone;
  step: number;
  total: number;
  showProgress: boolean;
  children: React.ReactNode;
  canBack?: boolean;
  canForward?: boolean;
  onBack?: () => void;
  onForward?: () => void;
  onReplay?: () => void;
}
export function SnakShell({
  tone,
  step,
  total,
  showProgress,
  children,
  canBack = false,
  canForward = false,
  onBack,
  onForward,
  onReplay
}: SnakShellProps) {
  const dark = tone === 'dark';
  const hasNav = Boolean(onBack || onForward || onReplay);
  return <div className="flex min-h-full w-full justify-center font-body antialiased" data-id="element-463" data-mp="fn5bm6wr01c4">
      <motion.div className="relative flex min-h-full w-full flex-col overflow-hidden" animate={{
      backgroundColor: dark ? '#01332b' : '#FBF7F0'
    }} transition={{
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-464" data-mp="3f8k4nvweavyz">
        {/* ambient brand shapes */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-465" data-mp="3v2enjwehiewy">
          <motion.div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-secondary blur-3xl" animate={{
          opacity: dark ? 0.14 : 0.45
        }} transition={{
          duration: 0.7
        }} data-id="element-466" data-mp="3q92belqzs0l3" />
          <motion.div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-primary blur-3xl" animate={{
          opacity: dark ? 0.12 : 0.16
        }} transition={{
          duration: 0.7
        }} data-id="element-467" data-mp="3ga300veudque" />
        </div>

        <header className="relative z-10 px-6 pb-3 pt-6" data-id="element-468" data-mp="32cxxv6i91c4f">
          <div className="flex items-center justify-between" data-id="element-469" data-mp="32eiud1o4j54x">
            <div className="flex items-center gap-2.5" data-id="element-470" data-mp="15j36ym95o9n8">
              <Logo2 size="sm" alt="Solventum" className="shrink-0" data-id="element-471" data-mp="10l1n3z3wo6ap" />
              <span className={['font-heading text-[15px] tracking-[-0.01em]', dark ? 'text-white' : 'text-background'].join(' ')} data-id="element-472" data-mp="10luk48d2bhfn">
                Solventum
              </span>
            </div>
            <span className={['font-body text-[10px] uppercase tracking-[0.22em]', dark ? 'text-primary' : 'text-[rgba(1,51,43,0.45)]'].join(' ')} data-id="element-473" data-mp="2l4ez456aa8uy">
              Filtek Snak
            </span>
          </div>

          {(showProgress || hasNav) && <div className="mt-4 flex items-center gap-2" data-id="element-474" data-mp="3iorn2cycmv91">
              {hasNav && <NavButton label="Previous step" dark={dark} disabled={!canBack} onClick={onBack} data-id="element-475" data-mp="33tbobumtqb6b">
                  <CaretLeft size={13} weight="bold" aria-hidden="true" data-id="element-476" data-mp="1scvag5qx42lk" />
                </NavButton>}

              {showProgress ? <div className="min-w-0 flex-1" data-id="element-477" data-mp="ffd5od7jndnh">
                  <ProgressRail step={step} total={total} tone={tone} data-id="element-478" data-mp="fe1q7g4qkhgj" />
                </div> : <span className="flex-1" data-id="element-479" data-mp="aiw4oif8x4sz" />}

              {hasNav && <>
                  <NavButton label="Next step" dark={dark} disabled={!canForward} onClick={onForward} data-id="element-480" data-mp="oe8aplyuqhp7">
                    <CaretRight size={13} weight="bold" aria-hidden="true" data-id="element-481" data-mp="3ssexq9sipuwt" />
                  </NavButton>
                  <NavButton label="Replay from the start" dark={dark} onClick={onReplay} data-id="element-482" data-mp="5hoygnsr4dof">
                    <ArrowCounterClockwise size={13} weight="bold" aria-hidden="true" data-id="element-483" data-mp="5jjhfwkgpvpv" />
                  </NavButton>
                </>}
            </div>}
        </header>

        <main className="relative z-10 flex flex-1 flex-col px-6 pb-8 pt-2" data-id="element-484" data-mp="394fk8r6kk9np">{children}</main>
      </motion.div>
    </div>;
}
function NavButton({
  children,
  label,
  dark,
  disabled = false,
  onClick
}: {
  children: React.ReactNode;
  label: string;
  dark: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return <button type="button" onClick={onClick} disabled={disabled || !onClick} aria-label={label} title={label} className={['inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors', 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary', dark ? 'border-white/20 text-white/70 hover:border-white/50 hover:text-white' : 'border-[rgba(1,51,43,0.18)] text-[rgba(1,51,43,0.6)] hover:border-[rgba(1,51,43,0.5)] hover:text-background', disabled || !onClick ? 'pointer-events-none opacity-25' : ''].join(' ')} data-id="element-485" data-mp="3rs3qco77shbb">
      {children}
    </button>;
}